/**
 * Navigation Configuration
 * FEA-211: 6-phase FMEA workflow navigation
 * IA restructure: 6-group information architecture surfacing all real pages.
 *
 * Workflow phases (numbered, part of the progress snake). Ordered along the
 * canonical authoring path; each phase carries a UNIQUE number that the
 * progress snake uses to derive complete/active/pending state:
 *   ① DEFINE   - Mission, Requirements
 *   ② MODEL    - Block Diagram, Interfaces, System States
 *   ③ ANALYZE  - Function/FM/RC Workshops, Cause & Effect, Scenarios,
 *                Scenario Comparison, Visualizations
 *   ④ MITIGATE - Mitigations Workshop, Mitigations (list), Software Logic,
 *                Stress Library
 *   ⑤ VERIFY   - Test Plans
 *   ⑥ REVIEW   - Risk Review, Workshop Summary, Coverage Matrix,
 *                Traceability, All Reports
 *
 * Cross-cutting group (no phase number, rendered as a visually secondary tier
 * below a separator):
 *   - Data & Admin - Templates, Baselines, Import, Backup, Audit,
 *                    Notifications, Users (admin-only), Settings
 */

import type { TerminologyConfig } from '@/config/terminology';

export interface NavItem {
  label: string;
  path: string;
  icon: string;
  testId?: string;
  badge?: number | string; // Optional badge (e.g., stub count)
  status?: 'complete' | 'in-progress' | 'not-started';
}

export interface NavPhase {
  id: string;
  number: number; // 1-6
  label: string;
  icon: string;
  items: NavItem[];
}

export interface NavGroup {
  id: string;
  label: string;
  icon: string;
  phase?: number; // 1-6 for workflow phases, undefined for cross-cutting groups
  disabled?: boolean; // Grey out the group to indicate it's not ready
  items: NavItem[];
}

/**
 * Badge data for dynamic navigation badges
 */
export interface NavigationBadges {
  mechanicalStubCount?: number;
  electricalStubCount?: number;
  operationalStubCount?: number;
  softwareLogicStubCount?: number;
}

/**
 * Phase number indicators (circled numbers)
 */
export const PHASE_NUMBERS = ['①', '②', '③', '④', '⑤', '⑥'] as const;

/**
 * Get the phase number indicator
 */
export function getPhaseNumber(phase: number): string {
  return PHASE_NUMBERS[phase - 1] || String(phase);
}

/**
 * Generate the 6-phase navigation structure
 * FEA-211: Workflow-aligned phases with meeting buttons
 */
export function getNavigationPhases(t: TerminologyConfig, badges?: NavigationBadges): NavPhase[] {
  return [
    // ========== DEFINE ==========
    {
      id: 'define',
      number: 1,
      label: 'DEFINE',
      icon: '🎯',
      items: [
        { label: 'Mission', path: '/mission', icon: '🎯', testId: 'nav-mission' },
        { label: t.requirements, path: '/requirements', icon: '📝', testId: 'nav-requirements' },
      ],
    },

    // ========== MODEL ==========
    {
      id: 'model',
      number: 2,
      label: 'MODEL',
      icon: '🧩',
      items: [
        // sysview-wave2 — the Block Diagram editor now lives on the Interlock
        // System View (edit mode); link straight there instead of bouncing
        // through the /components redirect.
        {
          label: 'Block Diagram',
          path: '/interlock/system',
          icon: '📦',
          testId: 'nav-components',
        },
        { label: 'Interfaces', path: '/interfaces', icon: '🔌', testId: 'nav-interfaces' },
        { label: 'System States', path: '/system-states', icon: '🔄', testId: 'nav-system-states' },
      ],
    },

    // ========== ANALYZE ==========
    {
      id: 'analyze',
      number: 3,
      label: 'ANALYZE',
      icon: '🔬',
      items: [
        {
          label: t.derivedFunctions,
          path: '/function-workshop',
          icon: '⚙️',
          testId: 'nav-function-workshop',
        },
        { label: t.failureModes, path: '/fm-workshop', icon: '⚠️', testId: 'nav-fm-workshop' },
        { label: t.rootCauses, path: '/causes-workshop', icon: '🔍', testId: 'nav-rc-workshop' },
        { label: 'Cause & Effect', path: '/cause-effect', icon: '🔗', testId: 'nav-cause-effect' },
        {
          label: 'Visualizations',
          path: '/visualizations',
          icon: '📊',
          testId: 'nav-visualizations',
        },
      ],
    },

    // ========== MITIGATE ==========
    {
      id: 'mitigate',
      number: 4,
      label: 'MITIGATE',
      icon: '🛡️',
      items: [
        {
          label: `${t.mitigations} Workshop`,
          path: '/mitigations-workshop',
          icon: '🔧',
          testId: 'nav-mitigations-workshop',
        },
        {
          label: `${t.mitigations} List`,
          path: '/mitigations',
          icon: '📋',
          testId: 'nav-mitigations',
        },
        {
          label: 'Software Logic',
          path: '/software-logic-workshop',
          icon: '💻',
          testId: 'nav-software-logic',
          badge:
            badges?.softwareLogicStubCount && badges.softwareLogicStubCount > 0
              ? badges.softwareLogicStubCount
              : undefined,
        },
        {
          label: 'Stress Library',
          path: '/stress-library',
          icon: '🏋️',
          testId: 'nav-stress-library',
        },
      ],
    },

    // ========== ⑤ VERIFY ==========
    // Ordered before REVIEW so the sidebar follows the canonical authoring path
    // DEFINE → MODEL → ANALYZE → MITIGATE → VERIFY → REVIEW. Each phase now has a
    // unique number, which the progress snake relies on for complete/active/pending.
    {
      id: 'verify',
      number: 5,
      label: 'VERIFY',
      icon: '🧪',
      items: [{ label: 'Test Plans', path: '/test-plans', icon: '🧪', testId: 'nav-test-plans' }],
    },

    // ========== ⑥ REVIEW ==========
    {
      id: 'review',
      number: 6,
      label: 'REVIEW',
      icon: '✅',
      items: [
        // F5: single canonical risk entry point. Replaces the former separate
        // "Risk Overview" and "Risk Report" sidebar entries (and the off-sidebar
        // Risk Dashboard / Risk Assessment / Executive / Completeness views) which
        // are now folded into the tabbed /risk-review page.
        { label: t.riskReview, path: '/risk-review', icon: '📊', testId: 'nav-risk-review' },
        {
          label: 'Workshop Summary',
          path: '/workshop-report',
          icon: '📋',
          testId: 'nav-workshop-report',
        },
        {
          label: 'Coverage Matrix',
          path: '/coverage-matrix',
          icon: '🎯',
          testId: 'nav-coverage-matrix',
        },
        {
          label: 'Traceability',
          path: '/traceability-report',
          icon: '🔗',
          testId: 'nav-traceability',
        },
        { label: 'All Reports', path: '/reports', icon: '📄', testId: 'nav-reports' },
      ],
    },
  ];
}

/**
 * Options for generating the cross-cutting additional groups.
 */
export interface AdditionalNavOptions {
  /** Whether the current user may manage users (admin). Gates the Users item. */
  isAdmin?: boolean;
}

/**
 * Generate additional navigation groups (non-phase / cross-cutting).
 *
 * The former "Reports" group is now the numbered ⑤ REVIEW phase; only the
 * cross-cutting "Data & Admin" utility group remains here (formerly "System").
 */
export function getAdditionalNavGroups(
  t: TerminologyConfig,
  opts: AdditionalNavOptions = {}
): NavGroup[] {
  const dataAdminItems: NavItem[] = [
    { label: 'Templates', path: '/templates', icon: '📑', testId: 'nav-templates' },
    {
      label: 'Template Library',
      path: '/template-library',
      icon: '📚',
      testId: 'nav-template-library',
    },
    {
      label: `${t.failureMode} Templates`,
      path: '/failure-mode-templates',
      icon: '🗂️',
      testId: 'nav-failure-mode-templates',
    },
    { label: 'Import Data', path: '/import', icon: '📥', testId: 'nav-import' },
    { label: 'Audit Trail', path: '/audit', icon: '📜', testId: 'nav-audit' },
    { label: 'Notifications', path: '/notifications', icon: '🔔', testId: 'nav-notifications' },
  ];

  return [
    // ========== DATA & ADMIN (cross-cutting) ==========
    {
      id: 'data-admin',
      label: 'Data & Admin',
      icon: '🗄️',
      items: dataAdminItems,
    },
  ];
}

/**
 * Legacy function: Generate navigation groups (backward compatibility)
 * Converts phases + additional groups to flat NavGroup array
 */
export function getNavigationGroups(t: TerminologyConfig, badges?: NavigationBadges): NavGroup[] {
  const phases = getNavigationPhases(t, badges);
  const additional = getAdditionalNavGroups(t);

  // Convert phases to groups
  const phaseGroups: NavGroup[] = phases.map((phase) => ({
    id: phase.id,
    label: phase.label,
    icon: phase.icon,
    phase: phase.number,
    items: phase.items,
  }));

  return [...phaseGroups, ...additional];
}

/**
 * Find which group contains a given path
 */
export function findGroupForPath(path: string, groups: NavGroup[]): NavGroup | undefined {
  return groups.find((group) => group.items.some((item) => item.path === path));
}

/**
 * Find which phase contains a given path
 */
export function findPhaseForPath(path: string, phases: NavPhase[]): NavPhase | undefined {
  return phases.find((phase) => phase.items.some((item) => item.path === path));
}

/**
 * Get all paths as a flat array
 */
export function getAllPaths(groups: NavGroup[]): string[] {
  return groups.flatMap((group) => group.items.map((item) => item.path));
}

/**
 * Get the phase number for a given path
 */
export function getPhaseForPath(path: string, groups: NavGroup[]): number | undefined {
  const group = findGroupForPath(path, groups);
  return group?.phase;
}

/**
 * Get phase completion status based on item statuses
 */
export function getPhaseStatus(phase: NavPhase): 'complete' | 'in-progress' | 'not-started' {
  const statuses = phase.items.map((item) => item.status).filter(Boolean);
  if (statuses.length === 0) return 'not-started';
  if (statuses.every((s) => s === 'complete')) return 'complete';
  if (statuses.some((s) => s === 'complete' || s === 'in-progress')) return 'in-progress';
  return 'not-started';
}

/**
 * Calculate total stub count across all phases
 */
export function getTotalStubCount(badges?: NavigationBadges): number {
  if (!badges) return 0;
  return (
    (badges.mechanicalStubCount || 0) +
    (badges.electricalStubCount || 0) +
    (badges.operationalStubCount || 0) +
    (badges.softwareLogicStubCount || 0)
  );
}
